import { ITheme } from '../models';
import { hyphenateSync as hyphenate } from 'hyphen/de';

/***
 * (?:         # begin non-capturing group
 *   \.        #   a dot
 *   (         #   begin capturing group (captures the actual extension)
 *     [^.]+   #     anything except a dot, multiple times
 *   )         #   end capturing group
 * )?          # end non-capturing group, make it optional
 * $           # anchor to the end of the string
 *
 * @param fileName
 * @return fileExtension
 */
export const getExtensionFromFilename = (fileName: string): string => {
  const re = /(?:.([^.]+))?$/;
  return re.exec(fileName)[1];
};

/***
 * @param filePath
 * @return fileName
 */
export const getNameFromFilePath = (filePath: string): string => {
  // eslint-disable-next-line
  const re = /[\/\\]([^\/\\]+)\.\w+$/;
  return re.exec(filePath)[1];
};

/**
 * Given an array of string or number values, returns a string representing a path
 * where each segment is joined by a forward slash, with any leading or trailing
 * slashes removed.
 *
 * @param segments - an array of string or number values
 * @return a string representing a path
 */
export const toAbsolutePath = (segments: (string | number)[]): string => {
  return (
    '/' +
    segments
      .map(String)
      .map(s => s.replace(/^\/+|\/+$/g, ''))
      .join('/')
  );
};

/**
 * example1 resident-saas://app/auth/reset-register/89892930-6d53-4593-q6d1-ede5cc25ed1b
 *
 * example2 resident-saas://app/auth/landing/VERIFY_EMAIL/SUCCESS/eyJhbGciOiJIUzI1NiJ9.eyJ1c2VyUHJvZmlsZUlkIjo3Njc1OTQ2MCwiZXhwaXJlZCI6MTcwNjAyMzAxOTA0NCwiY3JlYXRlZCI6MTcwNDgxMzQxOTA0NH0.ZqVbh1CYK87LS3SoYdYRxeNCvmAKd0Ewj8cOUcbD2Yo
 *
 * 'resident-saas://app' is the app identification;
 *
 * 'auth' is the route url to the authentication module;
 *
 * 'reset-register/89892930-6d53-4593-q6d1-ede5cc25ed1b' is the route url to the page we want to show to the user
 *  and the return value of the function
 *
 * @param deepLink
 * @return route
 */
export const deepLinkToRoute = (deepLink: string): string => {
  if (!deepLink) {
    return '';
  }

  // Parse the deep link to extract the route segment
  const urlPattern = /resident-saas:\/\/app\/(.+)/;
  const match = deepLink.match(urlPattern);

  if (match && match[1]) {
    return match[1]; // Return the extracted route part
  } else {
    console.error('Invalid deepLink format:', deepLink);
    return '';
  }
};

export const obfuscateString = (str = '', start = 4, end = -4, replaceChar = 'X'): string => {
  const slice = str.slice(start, end);
  return str.replace(slice, replaceChar.repeat(slice.length));
};

/**
 * returns true if the string contains HTML-tag elements
 * @param str
 */
export const containsHTML = (str: string) => {
  const htmlTagPattern = /<\/?[a-z][\s\S]*>/i; // Matches opening and closing tags
  return htmlTagPattern.test(str);
};

/**
 * returns new string without HTML-tag elements
 * @param str
 */
export const removeHTML = (
  str: string,
  isMobile = false,
  hyphenate = false,
  keepLineBreaksForMobile = false
): string => {
  // Guard the declared `: string` return type: callers (e.g. `removeHTML(x).length`)
  // crash if we hand back null/undefined when the input is missing.
  if (str == null) {
    return '';
  }

  let updatedString = str;

  if (isMobile) {
    if (containsHTML(str)) {
      if (keepLineBreaksForMobile) {
        // remove all html tags except <p> tags and replace with ' '
        updatedString = updatedString.replace(/<(?!\/?p\b)(?!p\b)[^>]+>/g, '').trim();

        // remove <p> tags and replace </p> tags with double line breaks
        updatedString = updatedString.replace(/<p>/g, '').replace(/<\/p>/g, '\n\n');
      } else {
        // remove all html tags and replace with ' '
        updatedString = str.replace(/<[^>]+>/g, ' ');

        // reduce several consecutive spaces to a single space
        updatedString = updatedString.replace(/\s+/g, ' ');
      }
      if (hyphenate) {
        return hyphen(updatedString.trim());
      }
      // remove whitespaces at the beginning and the end of the string
      return updatedString.trim();
    }

    if (hyphenate) {
      str = hyphen(str);
    }
    return str;
  }

  // for web, we keep the <p> tags for the linebreaks
  return updatedString.replace(/<(?!\/?p\b)(?!p\b)[^>]+>/g, '').trim();
};

export const removeWhitespace = (text: string): string => {
  return text.replace(/\s/g, '');
};
/**
 * Return a html string that uses the apps main font-family and the whitelabel primaryTextColor
 * @param htmlString
 */
export const htmlStyling = (htmlString: string, theme?: ITheme): string => {
  return `
      <html>
        <head>
          <style>
            a, a span {
              color: blue !important;
            }
            * {
              font-family: 'Source Sans Pro', sans-serif;
              color: ${theme.colorTextPrimary} !important;
              background-color: transparent !important;
            }
           body {
            overflow: hidden;
           }
          </style>
        </head>
        <body>
          <div>
            ${hyphen(htmlString)}
          </div>
        </body>
      </html>`;
};

export const snakeCaseToCamelCase = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/([-_][a-z])/g, group => group.toUpperCase().replace('-', '').replace('_', ''));
};

/**
 *  Returns the string after hyphening
 *  For now only in german
 * @param value
 */
export const hyphen = (value: string): string => {
  //TODO add english hyphenation for when multilingual app is integrated
  if (!value || typeof value !== 'string') {
    return '';
  }
  return hyphenate(value);
};

// supports non-latin chars
export const encodeBase64 = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  const stringToEncode = String.fromCodePoint(...bytes);
  return btoa(stringToEncode);
};

// supports non-latin chars
export const decodeBase64 = (value: string) => {
  try {
    const stringToDecode = atob(value);
    const bytes = Uint8Array.from(stringToDecode, m => m.codePointAt(0));
    return new TextDecoder().decode(bytes);
  } catch (e) {
    return atob(value);
  }
};
